import { NgIf } from '@angular/common';
import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TenantsAuthApi } from '@buspro/web/apis';
import { syncAutofilledInputs } from '@buspro/web/core';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzResultComponent } from 'ng-zorro-antd/result';

@Component({
  selector: 'buspro-admin-reset-password-request-form',
  templateUrl: './admin-reset-password-request-form.component.html',
  styleUrls: ['./admin-reset-password-request-form.component.scss'],
  providers: [NzNotificationService],
  imports: [ReactiveFormsModule, NzResultComponent, RouterLink, NzInputDirective, NgIf],
  standalone: true,
})
export class AdminResetPasswordRequestFormComponent {
  private fb = inject(FormBuilder);
  private tenantApi = inject(TenantsAuthApi);

  form = this.fb.group({
    email: [null, [Validators.required]],
  });
  submitted = false;
  /// The button is no longer disabled while the form is invalid, so it needs
  /// its own in-flight state to stop a second tap sending a second reset mail.
  isSubmitting = signal<boolean>(false);

  private resetFormRef = viewChild<ElementRef<HTMLFormElement>>('resetForm');

  constructor(private notification: NzNotificationService) {}

  submit() {
    if (this.isSubmitting()) return;

    // Adopt a browser-autofilled email — Chrome assigns input.value directly,
    // which Angular never sees.
    syncAutofilledInputs(this.form, this.resetFormRef()?.nativeElement);

    // Nothing validated this before: the disabled button was the only thing
    // stopping a request with a null email.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notification.error('Email required', 'Please provide your account email');
      return;
    }

    this.isSubmitting.set(true);
    this.tenantApi
      .tenantsAuthControllerForgotPassword({
        body: { email: this.form.get('email')!.value! },
      })
      .subscribe(
        () => {
          this.isSubmitting.set(false);
          this.submitted = true;
        },
        (error) => {
          this.isSubmitting.set(false);
          this.notification.error('Ops! Something went wrong', error.error?.errorMessage);
        },
      );
  }
}
